export const DEFAULT_API_GATEWAY_MAX_TIMEOUT = 29000;

/**
 * Normalize a cal-plat dttm string to the second-precision Z-suffixed form that
 * S3 storage and the cal-plat dataset GET endpoint expect.
 *
 * Cal-plat's API serializes `version_dttm` as microsecond+offset (e.g.
 * "2026-05-26T15:17:06.480741+00:00"), but the dataset GET endpoint and the
 * underlying S3 file basenames use second-precision Z (e.g.
 * "2026-05-26T15:17:06Z"). Pass either form here; you get back the canonical
 * S3 form. Cal-plat dttms are always UTC, so any non-Z offset is treated as
 * such and replaced with Z. Idempotent — passing an already-normalized dttm is
 * a no-op.
 *
 * Existing inline copies of this workaround live in aspen/InstallationDetails.js
 * and deviceLinking/store/reducers/deviceLinkingAppStoreSlice.ts — worth
 * back-porting them to this util in a follow-up. Remove the whole helper once
 * cal-plat's GET path tolerates either format (OBS-1996).
 */
export const normalizeCalibrationDttm = (dttm: string | null | undefined): string | null | undefined => {
    if (!dttm || typeof dttm !== "string") return dttm;
    return dttm
        // Strip sub-second precision (e.g. ".480741")
        .replace(/\.\d+/, "")
        // Replace any +HH:MM / -HH:MM / +HHMM offset with Z
        .replace(/([+-]\d{2}:?\d{2})$/, "Z");
};

/**
 * Render a cal-plat dttm as "YYYY-MM-DD HH:MM UTC" — the same format the
 * Progress Viewer's CompletedProcessesPanel uses for completed_at / started_at,
 * so date displays line up across the app instead of drifting between local
 * time and UTC.
 *
 * Returns null for null / empty / unparseable input so callers can conditionally render.
 */
export const formatCalibrationDttmUTC = (dttm: string | null | undefined): string | null => {
    if (!dttm) return null;
    const date = new Date(dttm);
    if (Number.isNaN(date.getTime())) return null;
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    return `${year}-${month}-${day} ${hours}:${minutes} UTC`;
};

/**
 * This function races the provided promise with a timeout promise that resolves
 * after the given timeout. If the timeout promise resolves first, the returned
 * promise resolves with the string "timeout".
 *
 * This function is used to manage the built-in timeout of AWS API gateway,
 * which is 30 seconds. In the case that timeout is reached, a 504 status code
 * will be returned, but we can't determine whether this was truly a result of
 * API Gateway or another server error. To solve this issue, we are just
 * checking for a 29 second delay (the API gateway times out in 30s), which then
 * tells us that we can display a status message instead of an error message.
 *
 * @param request The promise
 * @param timeoutMs The timeout in milliseconds
 *
 * @returns A promise that resolves with the result of the request or the string
 * "timeout"
 */
export function addTimeoutToPromise<T>(
    request: Promise<T>,
    timeoutMs: number = DEFAULT_API_GATEWAY_MAX_TIMEOUT
): Promise<T | "timeout"> {
    return Promise.race([
        request,
        new Promise<"timeout">((resolve) => {
            setTimeout(() => resolve("timeout"), timeoutMs);
        }),
    ]);
}
